/**
 * FinanceDetail + EditableValue + FinanceCropCard + CropComparisonTable
 * Extracted from DetailPage.jsx (A12 Sprint 8).
 *
 * Editable DB (contribution margin) calculator with per-crop cards,
 * what-if simulation mode, KTBL full-cost view, scenario builder,
 * rotation planner, benchmark integration, and comparison table.
 *
 * Co-extracted private helpers:
 *   - EditableValue (inline editable number with source badge)
 *   - FinanceCropCard (expandable per-crop DB I card with override editing)
 *   - CropComparisonTable (side-by-side crop comparison table)
 *
 * A6-26 fix: 3 raw fetch calls → apiClient.get (contribution-margin refresh)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../../lib/apiClient';
import SourceBadge from '../../components/SourceBadge';
import DBCalculatorFull from '../../components/DBCalculatorFull';
import ScenarioBuilder from '../../components/ScenarioBuilder';
import BenchmarkCard from '../../components/BenchmarkCard';
import RotationPlanner from '../../components/RotationPlanner';
import {
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Bar,
} from 'recharts';

const TOOLTIP_STYLE = {
  background: 'var(--aif-bg-elevated)',
  border: '1px solid var(--aif-border-default)',
  borderRadius: 12,
  fontSize: 12,
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--aif-text-muted)' };

// ─── EditableValue ───────────────────────────────────────────
function EditableValue({ value, unit, source, apiKey, onSave, whatIfMode, whatIfValue, onWhatIfChange, onWhatIfSimulate }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const displayValue = whatIfMode && whatIfValue !== undefined ? whatIfValue : value;

  const handleEdit = () => {
    setDraft(String(displayValue ?? value));
    setEditing(true);
  };

  const handleCommit = () => {
    const num = parseFloat(draft);
    if (!isNaN(num) && num !== value) {
      if (whatIfMode) {
        onWhatIfChange?.(apiKey, num);
        onWhatIfSimulate?.();
      } else {
        onSave?.(apiKey, num);
      }
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleCommit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        step="0.01"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        className="w-20 text-right text-xs bg-[var(--aif-bg-elevated)] border border-[var(--aif-color-primary)] rounded px-1 py-0.5 text-[var(--aif-text-primary)] outline-none"
      />
    );
  }

  return (
    <button
      onClick={handleEdit}
      className="group flex items-center gap-1 text-[var(--aif-text-primary)] hover:text-[var(--aif-color-primary)] transition-colors"
      title={t('finance.clickToEdit')}
    >
      <span className={`font-medium ${whatIfMode && whatIfValue !== undefined ? 'text-amber-400' : ''}`}>
        {(displayValue ?? value)?.toLocaleString()}
      </span>
      <span className="text-[var(--aif-text-muted)] text-[10px]">{unit}</span>
      <SourceBadge source={source} variant="compact" />
      <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--aif-color-primary)]">✎</span>
    </button>
  );
}

// ─── FinanceCropCard ─────────────────────────────────────────
function FinanceCropCard({ pf, currency, t, token, whatIfMode, whatIfDraft, whatIfResult, savingStatus, onSaveOverride, onResetCrop, onWhatIfChange, onWhatIfSimulate, onBenchmark }) {
  const [expanded, setExpanded] = useState(false);
  const ha = pf.per_ha || {};
  const sources = ha.sources || {};
  const vc = ha.variable_costs || {};

  // Use what-if result if available, otherwise use ha values
  const displayHa = whatIfResult || ha;
  const displayVc = displayHa.variable_costs || vc;

  // Count user overrides for this crop
  const userCount = Object.values(sources).filter(s => s === 'user_input').length;
  const hasUserData = userCount > 0;
  const userDataPct = pf.user_data_pct || 0;

  const dbIDelta = whatIfResult ? (whatIfResult.db_i - ha.db_i) : null;

  return (
    <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 text-left">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-[var(--aif-text-primary)]">{pf.crop_name || pf.field_name}</h4>
            {hasUserData && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--aif-color-primary)]/20 text-[var(--aif-color-primary)]">
                {t('finance.userDataPct', { pct: userDataPct })}
              </span>
            )}
            {savingStatus === 'saving' && <span className="text-[10px] text-[var(--aif-text-muted)] animate-pulse">...</span>}
            {savingStatus === 'saved' && <span className="text-[10px] text-green-400">✓</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[var(--aif-color-primary)]">
              {displayHa.db_i?.toLocaleString()} {currency}/ha
            </span>
            {dbIDelta !== null && (
              <span className={`text-[10px] font-bold ${dbIDelta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {dbIDelta >= 0 ? '+' : ''}{dbIDelta?.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--aif-text-muted)]">
          <span className="text-green-400">{t('finance.revenue')}: {displayHa.revenue?.toLocaleString()}</span>
          <span className="text-red-400">{t('finance.variableCosts')}: {displayVc.total?.toLocaleString()}</span>
          {pf.area_ha && <span className="ml-auto">{pf.area_ha} ha</span>}
          <svg className={`w-3.5 h-3.5 text-[var(--aif-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-[var(--aif-border-default)] pt-3 space-y-2">
          {/* Revenue section */}
          <div className="text-xs space-y-2">
            <div className="font-semibold text-green-400 uppercase text-[10px] mb-1">{t('finance.revenue')}</div>

            {/* Yield */}
            <div className="flex justify-between items-center">
              <span className="text-[var(--aif-text-muted)]">{t('finance.yieldLabel')}</span>
              <EditableValue
                value={ha.yield_t_ha}
                unit="t/ha"
                source={sources.yield || 'reference'}
                apiKey="yield_qty"
                onSave={onSaveOverride}
                whatIfMode={whatIfMode}
                whatIfValue={whatIfDraft['yield_qty']}
                onWhatIfChange={onWhatIfChange}
                onWhatIfSimulate={onWhatIfSimulate}
              />
            </div>
            {/* Price */}
            <div className="flex justify-between items-center">
              <span className="text-[var(--aif-text-muted)]">{t('finance.priceLabel')}</span>
              <EditableValue
                value={ha.market_price_t}
                unit={`${currency}/t`}
                source={sources.price || 'reference'}
                apiKey="price_per_unit"
                onSave={onSaveOverride}
                whatIfMode={whatIfMode}
                whatIfValue={whatIfDraft['price_per_unit']}
                onWhatIfChange={onWhatIfChange}
                onWhatIfSimulate={onWhatIfSimulate}
              />
            </div>
            {/* Grain revenue line */}
            <div className="flex justify-between items-center text-[var(--aif-text-muted)]">
              <span>= {t('finance.grainRevenue')}</span>
              <span className="text-[var(--aif-text-primary)]">
                {((displayHa.yield_t_ha ?? ha.yield_t_ha) * (displayHa.market_price_t ?? ha.market_price_t))?.toLocaleString()}
              </span>
            </div>
            {/* Subsidy */}
            <div className="flex justify-between items-center">
              <span className="text-[var(--aif-text-muted)]">+ {t('finance.subsidy')}</span>
              <EditableValue
                value={ha.subsidy_per_ha}
                unit={`${currency}/ha`}
                source={sources.subsidy || 'reference'}
                apiKey="subsidy_per_ha"
                onSave={onSaveOverride}
                whatIfMode={whatIfMode}
                whatIfValue={whatIfDraft['subsidy_per_ha']}
                onWhatIfChange={onWhatIfChange}
                onWhatIfSimulate={onWhatIfSimulate}
              />
            </div>
            {/* Revenue total */}
            <div className="flex justify-between items-center font-semibold border-t border-[var(--aif-border-default)] pt-1">
              <span className="text-green-400">= {t('finance.revenue')}</span>
              <span className="text-green-400">{displayHa.revenue?.toLocaleString()}</span>
            </div>
          </div>

          {/* Variable costs section */}
          <div className="text-xs space-y-2 mt-2">
            <div className="font-semibold text-red-400 uppercase text-[10px] mb-1">{t('finance.variableCosts')}</div>
            {[
              ['seed', t('finance.seed'), 'seed_cost'],
              ['fertilizer', t('finance.fertilizer'), 'fertilizer_cost'],
              ['plant_protection', t('finance.ppp'), 'ppp_cost'],
              ['machinery_variable', t('finance.machinery'), 'machinery_cost'],
              ['contractor', t('finance.contractor'), 'contractor_cost'],
              ['drying', t('finance.drying'), 'drying_cost'],
              ['insurance_crop', t('finance.insurance'), 'insurance_cost'],
              ['miscellaneous', t('finance.misc'), 'misc_cost'],
            ].map(([vcKey, label, apiKey]) => (
              vc[vcKey] > 0 ? (
                <div key={vcKey} className="flex justify-between items-center">
                  <span className="text-[var(--aif-text-muted)]">{label}</span>
                  <EditableValue
                    value={vc[vcKey]}
                    unit={`${currency}/ha`}
                    source={sources[vcKey === 'miscellaneous' ? 'miscellaneous' : vcKey.replace('_variable','').replace('_crop','')] || 'reference'}
                    apiKey={apiKey}
                    onSave={onSaveOverride}
                    whatIfMode={whatIfMode}
                    whatIfValue={whatIfDraft[apiKey]}
                    onWhatIfChange={onWhatIfChange}
                    onWhatIfSimulate={onWhatIfSimulate}
                  />
                </div>
              ) : null
            ))}
            <div className="flex justify-between items-center font-semibold border-t border-[var(--aif-border-default)] pt-1">
              <span className="text-red-400">= {t('finance.variableCosts')}</span>
              <span className="text-red-400">{displayVc.total?.toLocaleString()}</span>
            </div>
          </div>

          {/* DB I result */}
          <div className="border-t border-[var(--aif-border-default)] pt-2 mt-2">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-[var(--aif-text-primary)]">= {t('finance.dbI')}</span>
              <div className="flex items-center gap-2">
                <span className="text-[var(--aif-color-primary)]">{displayHa.db_i?.toLocaleString()} {currency}/ha</span>
                {dbIDelta !== null && (
                  <span className={`text-[10px] ${dbIDelta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ({dbIDelta >= 0 ? '+' : ''}{dbIDelta?.toLocaleString()}, {dbIDelta !== 0 && ha.db_i ? `${Math.round((dbIDelta/ha.db_i)*100)}%` : '0%'})
                  </span>
                )}
              </div>
            </div>
            {pf.area_ha && pf.total && (
              <div className="text-xs text-[var(--aif-text-muted)] mt-1">
                {t('finance.total')}: {(displayHa.db_i * pf.area_ha)?.toLocaleString()} {currency} ({pf.area_ha} ha)
              </div>
            )}
          </div>

          {/* Reset link */}
          {hasUserData && !whatIfMode && (
            <div className="pt-1">
              <button
                onClick={onResetCrop}
                className="text-[10px] text-[var(--aif-text-muted)] hover:text-red-400 transition-colors underline"
              >
                ↩ {t('finance.resetToRef')}
              </button>
            </div>
          )}

          {/* Sprint 56: Benchmark button */}
          {onBenchmark && (
            <button
              onClick={(e) => { e.stopPropagation(); onBenchmark(); }}
              className="w-full mt-3 py-2 rounded-xl border border-[var(--aif-border-default)] text-[var(--aif-text-secondary)] text-xs font-medium hover:bg-[var(--aif-bg-elevated)] transition-colors flex items-center justify-center gap-1.5"
            >
              <span>📊</span>
              <span>{t('benchmark.title')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CropComparisonTable ─────────────────────────────────────
function CropComparisonTable({ sorted, currency, whatIfResults, t }) {
  if (sorted.length < 2) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">
        {t('finance.comparisonTitle')}
      </h3>
      <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
        <table className="w-full text-[11px] min-w-[480px]">
          <thead>
            <tr className="bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] uppercase text-[9px] tracking-wide">
              <th className="text-left p-2 pl-3">{t('finance.cropLabel')}</th>
              <th className="text-right p-2">{t('finance.comparisonArea')}</th>
              <th className="text-right p-2">{t('finance.comparisonYield')}</th>
              <th className="text-right p-2">{t('finance.comparisonPrice')}</th>
              <th className="text-right p-2">{t('finance.comparisonRevenue')}</th>
              <th className="text-right p-2">{t('finance.comparisonCosts')}</th>
              <th className="text-right p-2">{t('finance.comparisonDbI')}</th>
              <th className="text-right p-2 pr-3">{t('finance.comparisonDbITotal')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((pf, i) => {
              const ha = pf.per_ha || {};
              const wi = whatIfResults[pf.crop_code];
              const displayHa = wi || ha;
              const sources = ha.sources || {};
              const dbITotal = Math.round((displayHa.db_i || 0) * (pf.area_ha || 0));
              const hasWhatIf = !!wi;
              return (
                <tr key={i} className={`border-t border-[var(--aif-border-default)] ${hasWhatIf ? 'bg-amber-500/5' : ''}`}>
                  <td className="p-2 pl-3 text-[var(--aif-text-primary)] font-medium whitespace-nowrap">
                    {pf.crop_name || pf.field_name}
                  </td>
                  <td className="text-right p-2 text-[var(--aif-text-muted)]">{pf.area_ha} ha</td>
                  <td className="text-right p-2">
                    <span className={`${sources.yield === 'user_input' ? 'text-[var(--aif-color-primary)]' : 'text-[var(--aif-text-muted)]'}`}>
                      {ha.yield_t_ha} <SourceBadge source={sources.yield || 'reference'} variant="compact" />
                    </span>
                  </td>
                  <td className="text-right p-2">
                    <span className={`${sources.price === 'user_input' ? 'text-[var(--aif-color-primary)]' : sources.price === 'market_live' ? 'text-blue-400' : 'text-[var(--aif-text-muted)]'}`}>
                      {ha.market_price_t} <SourceBadge source={sources.price || 'reference'} variant="compact" />
                    </span>
                  </td>
                  <td className="text-right p-2 text-[var(--aif-text-primary)]">{displayHa.revenue?.toLocaleString()}</td>
                  <td className="text-right p-2 text-red-400">{displayHa.variable_costs?.total?.toLocaleString()}</td>
                  <td className={`text-right p-2 font-bold ${hasWhatIf ? 'text-amber-400' : 'text-[var(--aif-color-primary)]'}`}>
                    {displayHa.db_i?.toLocaleString()}
                  </td>
                  <td className="text-right p-2 pr-3 text-[var(--aif-text-primary)]">
                    {dbITotal.toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── FinanceDetail (main) ────────────────────────────────────
export default function FinanceDetail({ finance, token }) {
  const { t } = useTranslation();
  const [localFinance, setLocalFinance] = useState(finance);
  const [whatIfMode, setWhatIfMode] = useState(false);
  const [whatIfDrafts, setWhatIfDrafts] = useState({});
  const [whatIfResults, setWhatIfResults] = useState({});
  const [savingStatus, setSavingStatus] = useState({});
  const [ktblCrop, setKtblCrop] = useState(null);
  const [showScenario, setShowScenario] = useState(false);
  const [benchmarkCrop, setBenchmarkCrop] = useState(null);
  const [showRotationPlanner, setShowRotationPlanner] = useState(false);

  useEffect(() => { setLocalFinance(finance); }, [finance]);

  if (!localFinance?.per_field?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-4xl mb-4">💶</div>
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">{t('finance.title')}</h3>
        <p className="text-xs text-[var(--aif-text-muted)] max-w-[250px]">{t('finance.noData')}</p>
      </div>
    );
  }

  const currency = localFinance.currency || 'EUR';
  const sorted = [...localFinance.per_field].sort((a, b) => (b.per_ha?.db_i || 0) - (a.per_ha?.db_i || 0));

  const chartData = sorted.map(f => ({
    name: f.crop_name || f.field_name,
    db_i: whatIfMode && whatIfResults[f.crop_code]
      ? whatIfResults[f.crop_code].db_i
      : (f.per_ha?.db_i || 0),
    revenue: f.per_ha?.revenue || 0,
    costs: f.per_ha?.variable_costs?.total || 0,
  }));

  const overallUserPct = localFinance.overall_user_data_pct || 0;
  const avgRefPct = sorted.length > 0
    ? Math.round(sorted.reduce((s, pf) => s + (pf.per_ha?.reference_pct ?? 100), 0) / sorted.length)
    : 100;

  const totalOverrideCount = sorted.reduce((sum, pf) => {
    const sources = pf.per_ha?.sources || {};
    return sum + Object.values(sources).filter(s => s === 'user_input').length;
  }, 0);
  const totalValueCount = sorted.reduce((sum, pf) => {
    return sum + Object.keys(pf.per_ha?.sources || {}).length;
  }, 0);

  const refreshContributionMargin = async () => {
    try {
      const fresh = await apiClient.get('/api/v1/aif/finance/contribution-margin');
      setLocalFinance(fresh);
    } catch { /* ignore */ }
  };

  const handleSaveOverride = async (crop_code, apiKey, value) => {
    if (!token) return;
    setSavingStatus(s => ({ ...s, [crop_code]: 'saving' }));
    try {
      await apiClient.patch(`/api/v1/aif/finance/overrides/${crop_code}`, { [apiKey]: parseFloat(value) }, {
        entityType: 'finance_override', queueOnOffline: true,
      });
      await refreshContributionMargin();
      setSavingStatus(s => ({ ...s, [crop_code]: 'saved' }));
      setTimeout(() => setSavingStatus(s => ({ ...s, [crop_code]: null })), 1500);
    } catch { setSavingStatus(s => ({ ...s, [crop_code]: null })); }
  };

  const handleResetCrop = async (crop_code) => {
    if (!token) return;
    try {
      await apiClient.delete(`/api/v1/aif/finance/overrides/${crop_code}`, {
        entityType: 'finance_override', queueOnOffline: true,
      });
      await refreshContributionMargin();
    } catch { /* ignore */ }
  };

  const handleWhatIfSimulate = async (crop_code) => {
    if (!token) return;
    const drafts = whatIfDrafts[crop_code] || {};
    if (Object.keys(drafts).length === 0) return;
    try {
      try {
        const r = await apiClient.post('/api/v1/aif/finance/overrides/what-if', { crop_code, what_if: drafts }, {
          entityType: 'finance_override', queueOnOffline: false,
        });
        if (r?.data?.per_ha) setWhatIfResults(rr => ({ ...rr, [crop_code]: r.data.per_ha }));
      } catch (err) {
        if (!(err instanceof ApiError)) throw err;
      }
    } catch { /* ignore */ }
  };

  const handleSaveAllWhatIf = async () => {
    if (!token) return;
    try {
      for (const [crop_code, drafts] of Object.entries(whatIfDrafts)) {
        if (Object.keys(drafts).length > 0) {
          await apiClient.patch(`/api/v1/aif/finance/overrides/${crop_code}`, drafts, {
            entityType: 'finance_override', queueOnOffline: true,
          });
        }
      }
      await refreshContributionMargin();
      setWhatIfMode(false);
      setWhatIfDrafts({});
      setWhatIfResults({});
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  const handleResetWhatIf = () => {
    setWhatIfDrafts({});
    setWhatIfResults({});
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="text-center py-3">
        <div className="text-3xl font-bold text-[var(--aif-color-primary)]">
          {(localFinance.avg_db_i_per_ha || 0).toLocaleString()} {currency}/ha
        </div>
        <div className="text-xs text-[var(--aif-text-muted)] mt-1">
          {t('finance.avgPerHa')} | {t('finance.total')}: {(localFinance.farm_total?.db_i || 0).toLocaleString()} {currency}
        </div>
        <div className="text-xs text-[var(--aif-text-muted)] mt-1">
          {localFinance.total_area_ha} ha | {sorted.length} {t('dashboard.crops')} | {localFinance.data_source === 'fields' ? t('finance.fromFields') : t('finance.fromProfile')}
        </div>
      </div>

      {/* Override summary bar */}
      {totalOverrideCount > 0 ? (
        <div className="rounded-xl bg-[var(--aif-color-primary)]/10 border border-[var(--aif-color-primary)]/30 px-3 py-2 text-xs text-[var(--aif-color-primary)] flex items-center gap-2">
          <span>✏️</span>
          <span>{t('finance.overrideSummary', { count: totalOverrideCount, total: totalValueCount, pct: overallUserPct })}</span>
        </div>
      ) : (
        <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] px-3 py-2 text-xs text-[var(--aif-text-muted)] flex items-center gap-2">
          <span>📊</span>
          <span>{t('finance.noOverrides')}</span>
        </div>
      )}

      {/* Sprint 55: Scenario Builder button */}
      <button
        onClick={() => setShowScenario(true)}
        className="w-full py-2.5 rounded-xl border border-dashed border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)] text-sm font-medium hover:bg-[var(--aif-color-primary)]/5 transition-colors flex items-center justify-center gap-2"
      >
        <span>🔮</span>
        <span>{t('scenario.title')}</span>
      </button>

      {/* Sprint 57: Rotation Planner button */}
      <button
        onClick={() => setShowRotationPlanner(true)}
        className="w-full py-2.5 rounded-xl border border-dashed border-green-500/50 text-green-400 text-sm font-medium hover:bg-green-500/5 transition-colors flex items-center justify-center gap-2"
        data-testid="open-rotation-planner"
      >
        <span>🌱</span>
        <span>{t('rotation.openButton')}</span>
      </button>

      {showScenario && <ScenarioBuilder token={token} onClose={() => setShowScenario(false)} />}

      {/* Sprint 56: Benchmark for selected crop */}
      {benchmarkCrop && (
        <BenchmarkCard
          token={token}
          cropCode={benchmarkCrop.code}
          cropName={benchmarkCrop.name}
          onClose={() => setBenchmarkCrop(null)}
        />
      )}

      {/* Sprint 57: Rotation Planner */}
      {showRotationPlanner && (
        <RotationPlanner token={token} onClose={() => setShowRotationPlanner(false)} />
      )}

      {/* View toggle: DB Overview | KTBL Vollkosten */}
      <div className="flex rounded-xl border border-[var(--aif-border-default)] overflow-hidden">
        <button
          onClick={() => setKtblCrop(null)}
          className={`flex-1 py-2 text-xs font-medium transition-colors ${
            ktblCrop === null
              ? 'bg-[var(--aif-color-primary)] text-white'
              : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)]'
          }`}
        >
          {t('finance.simpleView')}
        </button>
        <button
          onClick={() => { if (ktblCrop === null) setKtblCrop(sorted[0]?.crop_code || null); }}
          className={`flex-1 py-2 text-xs font-medium transition-colors ${
            ktblCrop !== null
              ? 'bg-[var(--aif-color-primary)] text-white'
              : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)]'
          }`}
        >
          {t('finance.ktblView')}
        </button>
      </div>

      {/* KTBL Full Cost Accordion */}
      {ktblCrop !== null && (
        <div className="space-y-2">
          {sorted.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {sorted.map(pf => (
                <button
                  key={pf.crop_code}
                  onClick={() => setKtblCrop(pf.crop_code)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors ${
                    ktblCrop === pf.crop_code
                      ? 'bg-[var(--aif-color-primary)] border-[var(--aif-color-primary)] text-white'
                      : 'bg-[var(--aif-bg-surface)] border-[var(--aif-border-default)] text-[var(--aif-text-secondary)]'
                  }`}
                >
                  {pf.crop_name || pf.crop_code}
                </button>
              ))}
            </div>
          )}
          <DBCalculatorFull
            token={token}
            cropCode={ktblCrop}
            cropName={sorted.find(p => p.crop_code === ktblCrop)?.crop_name || ktblCrop}
            currency={currency}
          />
        </div>
      )}

      {/* What-if mode toggle */}
      <div className={`flex items-center gap-2 ${ktblCrop !== null ? 'hidden' : ''}`}>
        <button
          onClick={() => { setWhatIfMode(m => !m); if (whatIfMode) { setWhatIfDrafts({}); setWhatIfResults({}); } }}
          className={`flex-1 py-2 px-3 rounded-xl text-xs font-medium border transition-all ${
            whatIfMode
              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
              : 'bg-[var(--aif-bg-surface)] border-[var(--aif-border-default)] text-[var(--aif-text-secondary)]'
          }`}
        >
          {whatIfMode ? `⚡ ${t('finance.whatIfActive')}` : `🔮 ${t('finance.whatIfTitle')}`}
        </button>
        {whatIfMode && (
          <>
            <button
              onClick={handleResetWhatIf}
              className="py-2 px-3 rounded-xl text-xs border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)]"
            >
              {t('finance.whatIfReset')}
            </button>
            <button
              onClick={handleSaveAllWhatIf}
              className="py-2 px-3 rounded-xl text-xs font-medium bg-[var(--aif-color-primary)] text-white"
            >
              {t('finance.whatIfSave')}
            </button>
          </>
        )}
      </div>
      {ktblCrop === null && whatIfMode && (
        <p className="text-[11px] text-[var(--aif-text-muted)] -mt-4">{t('finance.whatIfHint')}</p>
      )}

      {/* Bar chart — only in DB overview */}
      {ktblCrop === null && (
      <div>
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-3 uppercase tracking-wide">
          {t('finance.dbI')} {t('finance.perHa')}
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
              <XAxis type="number" tick={AXIS_TICK} />
              <YAxis dataKey="name" type="category" tick={AXIS_TICK} width={80} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="db_i" fill="var(--aif-color-primary)" radius={[0, 4, 4, 0]} name={`${t('finance.dbI')} ${currency}/ha`} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Per-crop editable cards + comparison table + reference note — DB Overview only */}
      {ktblCrop === null && (
      <>
      <div className="space-y-3">
        {sorted.map((pf, i) => (
          <FinanceCropCard
            key={i}
            pf={pf}
            currency={currency}
            t={t}
            token={token}
            whatIfMode={whatIfMode}
            whatIfDraft={whatIfDrafts[pf.crop_code] || {}}
            whatIfResult={whatIfResults[pf.crop_code] || null}
            savingStatus={savingStatus[pf.crop_code] || null}
            onSaveOverride={(apiKey, value) => handleSaveOverride(pf.crop_code, apiKey, value)}
            onResetCrop={() => handleResetCrop(pf.crop_code)}
            onWhatIfChange={(apiKey, value) => {
              setWhatIfDrafts(d => ({
                ...d,
                [pf.crop_code]: { ...(d[pf.crop_code] || {}), [apiKey]: value },
              }));
            }}
            onWhatIfSimulate={() => handleWhatIfSimulate(pf.crop_code)}
            onBenchmark={() => setBenchmarkCrop({ code: pf.crop_code, name: pf.crop_name })}
          />
        ))}
      </div>

      {/* Crop comparison table */}
      <CropComparisonTable sorted={sorted} currency={currency} whatIfResults={whatIfResults} t={t} />

      {/* Reference data hint */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <SourceBadge source="reference" variant="compact" />
          <span className="text-xs text-[var(--aif-text-secondary)] font-medium">{t('finance.sourceHint')}</span>
        </div>
        <p className="text-[11px] text-[var(--aif-text-muted)] leading-relaxed">
          {t('finance.refPctHint', { pct: avgRefPct })}
        </p>
        <p className="text-[11px] text-[var(--aif-text-muted)] leading-relaxed mt-1 border-t border-[var(--aif-border-default)] pt-1">
          {t('finance.referenceDataSource')}
        </p>
      </div>
      </>
      )}
    </div>
  );
}
